// Structured Season Team classification (028) — one centralized place for
// the option lists, correct display ordering, and the human-readable
// formatter every page should call instead of re-implementing this. See
// migration 028's own header comments for the full schema/authority
// design this supports.
//
// classification_type: 'grade' | 'u_based' | 'age' | 'named_division' | null
// classification_values: text[] — always stored/displayed in the ORDER
// given below for grade/u_based/age (logical/numeric order, never
// lexical — "9" must sort before "10"). named_division always holds
// exactly one value, including a free-typed "Other" string with no
// sentinel and no separate custom-text column.

export const CLASSIFICATION_TYPES = ['grade', 'u_based', 'age', 'named_division'];

export const CLASSIFICATION_TYPE_LABELS = {
  grade: 'Grade',
  u_based: 'U-Based',
  age: 'Age',
  named_division: 'Named Division',
};

// K–3rd added (029); 4th–8th unchanged from 028. Order here IS the
// canonical display/sort order — K correctly sorts before 1st.
export const GRADE_OPTIONS = ['K', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];

export const U_BASED_OPTIONS = ['5U', '6U', '7U', '8U', '9U', '10U', '11U', '12U', '13U', '14U'];

export const AGE_OPTIONS = ['5', '6', '7', '8', '9', '10', '11', '12', '13', '14'];

// "Other" is handled specially by the UI — it is never itself stored in
// classification_values; selecting it reveals a text field whose typed
// value is stored directly as the (single) classification_values entry.
export const NAMED_DIVISION_OPTIONS = ['Peewee', 'Minor', 'Junior Varsity', 'Varsity'];
export const NAMED_DIVISION_OTHER = 'Other';

// Initial Football Format options (players_per_side is the authoritative
// integer; these are just the initial picker choices). "Other" reveals a
// numeric input whose value is stored directly as players_per_side.
export const FORMAT_OPTIONS = [
  { label: '7v7', value: 7 },
  { label: '8v8', value: 8 },
  { label: '9v9', value: 9 },
  { label: '10v10', value: 10 },
  { label: '11v11', value: 11 },
];

function optionListFor(type) {
  if (type === 'grade') return GRADE_OPTIONS;
  if (type === 'u_based') return U_BASED_OPTIONS;
  if (type === 'age') return AGE_OPTIONS;
  return null;
}

/** Sorts classification_values into the correct logical/display order for
 * a given type. Named Division never has more than one value, so it's
 * returned as-is. For grade/u_based/age, sorts by each option's position
 * in the fixed canonical list above — never lexical string sort, which
 * would put "10U" before "9U". Values that aren't recognized options
 * (shouldn't normally happen) sort after all recognized ones, stably. */
export function sortClassificationValues(type, values) {
  const list = Array.isArray(values) ? values : [];
  const options = optionListFor(type);
  if (!options) return list;
  return [...list].sort((a, b) => {
    const ia = options.indexOf(a);
    const ib = options.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/** Human-readable display string for a classification_type/values pair,
 * e.g. "5th–6th Grade", "10U–11U", "Ages 8–9", "Varsity", or a custom
 * Named Division string. Returns null if there's no structured
 * classification (type is null/values empty) — callers should fall back
 * to the legacy grade_age_group text in that case. */
export function formatTeamClassification(type, values) {
  const sorted = sortClassificationValues(type, values);
  if (!type || sorted.length === 0) return null;

  if (type === 'named_division') {
    return sorted[0];
  }

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const range = sorted.length === 1 ? first : `${first}–${last}`;

  if (type === 'grade') {
    // "K" alone already reads as a complete, self-explanatory label —
    // "K Grade" would be redundant. Every other single grade value (and
    // every multi-value range, including one that starts at K, e.g.
    // "K–1st Grade") keeps the " Grade" suffix, unchanged from 028.
    if (sorted.length === 1 && first === 'K') return 'K';
    return `${range} Grade`;
  }
  if (type === 'u_based') return range;
  if (type === 'age') return sorted.length === 1 ? `Age ${first}` : `Ages ${first}–${last}`;
  return range;
}

/** The one centralized display function every page should call for a
 * team's classification — prefers structured classification_type/
 * classification_values (028) when present, otherwise falls back to the
 * legacy grade_age_group text (009). Never guesses/converts between the
 * two. */
export function classificationDisplay(team) {
  if (!team) return null;
  const formatted = formatTeamClassification(team.classification_type, team.classification_values);
  return formatted || team.grade_age_group || null;
}

/** Human-readable Football Format string, e.g. "8v8". Returns null when
 * players_per_side is not set — callers decide whether/how to show that
 * absence (never a false "0v0" or placeholder value). */
export function playersPerSideDisplay(playersPerSide) {
  if (playersPerSide === null || playersPerSide === undefined) return null;
  return `${playersPerSide}v${playersPerSide}`;
}

// football_type (029) — independent from classification/players_per_side/
// season_status/teams.status. Never inferred from any other field. Only
// 'flag'/'tackle' are supported in this phase — no "Other".
export const FOOTBALL_TYPE_OPTIONS = ['flag', 'tackle'];

export const FOOTBALL_TYPE_LABELS = {
  flag: 'Flag',
  tackle: 'Tackle',
};

/** Human-readable football_type string, e.g. "Tackle". Returns null when
 * unset (legacy Team) — callers omit the segment entirely rather than
 * showing placeholder text like "Unknown Football Type". Never exposes
 * the raw stored string ('flag'/'tackle'). */
export function footballTypeDisplay(footballType) {
  if (!footballType) return null;
  return FOOTBALL_TYPE_LABELS[footballType] || null;
}

/** The one centralized "Team definition" summary line — Classification ·
 * Football Type · Season · Format — used by TeamDetailPage/MyTeamsPage
 * and any future page that wants this exact composition. Omits any
 * segment that isn't set (never a placeholder like "Unknown Football
 * Type"), so a legacy Team with no football_type still reads cleanly,
 * e.g. "6th Grade · 2026 · 11v11". Pages with a different layout (e.g.
 * ProgramDashboardPage's card, which places classification next to the
 * team name rather than in this line) compose their own subset instead
 * of using this function — see their own comments. */
export function formatTeamSummaryLine(team) {
  if (!team) return '';
  return [
    classificationDisplay(team),
    footballTypeDisplay(team.football_type),
    team.season,
    playersPerSideDisplay(team.players_per_side),
  ].filter(part => part !== null && part !== undefined && part !== '').join(' · ');
}
